/**
 * Feature storage service — CRUD for Feature entities in the graph model.
 *
 * Features always live in layer groups. The path is:
 *   Entity → has-layer-group → LayerGroup → has-feature → Feature
 *
 * No entity directly owns features — all queries go through layer groups.
 */

import { db } from '../database/schema.js'
import type { Feature, FeatureType } from '../schemas/feature.js'
import { OfflineFirstStorageService, type StorageResult } from './base-storage.js'
import { validateFeature, runValidation } from './validation.js'
import { edgeService, nodeRef, parseNodeRef } from '../services/edge-service.js'

export interface CreateFeatureData {
  name: string
  description?: string | undefined
  type: FeatureType
  geometry: GeoJSON.Geometry
  properties?: Record<string, unknown> | undefined
  style?: Feature['style'] | undefined
  label?: Feature['label'] | undefined
  tags?: string[] | undefined
  isVisible?: boolean | undefined
  waypointType?: string | undefined
  createdBy: string
}

export interface AddFeatureToEntityOptions {
  /** The entity node type: 'route', 'plan', 'activity', or 'user' */
  entityType: string
  /** The entity ID */
  entityId: string
  /** The layer group ID to place the feature in. If omitted, uses entity's root group. */
  groupId?: string | undefined
}

class FeatureStorageService extends OfflineFirstStorageService<Feature> {
  constructor() {
    super(db.features, 'feature')
  }

  /**
   * Create a feature and wire it to a layer group.
   */
  async createFeature(
    data: CreateFeatureData,
    target: AddFeatureToEntityOptions
  ): Promise<StorageResult<Feature>> {
    const featureData = {
      name: data.name,
      description: data.description,
      type: data.type,
      geometry: data.geometry,
      properties: data.properties,
      style: data.style,
      label: data.label,
      tags: data.tags ?? [],
      isVisible: data.isVisible ?? true,
      waypointType: data.waypointType,
      createdBy: data.createdBy,
    }

    const validation = runValidation(validateFeature, featureData, { context: 'create' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    const result = await this.create(featureData as Omit<Feature, 'id' | 'createdAt' | 'updatedAt'>)
    if (!result.success || !result.data) return result

    const featureRef = nodeRef('feature', result.data.id)
    const groupId = target.groupId ?? (await this.resolveRootGroupId(target))
    if (groupId) {
      await edgeService.create({
        subject: nodeRef('group', groupId),
        predicate: 'has-feature',
        object: featureRef,
      })
      await this.table.update(result.data.id, { layerId: groupId })
      result.data.layerId = groupId
      const group = await db.layerGroups.get(groupId)
      if (group) {
        await db.layerGroups.update(groupId, {
          featureIds: [...group.featureIds, result.data.id],
        })
      }
    }

    return result
  }

  async updateFeature(id: string, updates: Partial<Feature>): Promise<StorageResult<Feature>> {
    const validation = runValidation(validateFeature, updates, { context: 'update' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }
    return this.update(id, updates)
  }

  /**
   * Delete a feature and all its edges.
   */
  async deleteFeature(id: string): Promise<StorageResult<void>> {
    await edgeService.deleteAllForNode(nodeRef('feature', id))
    const result = await this.deleteById(id)
    if (result.success) {
      this.createSyncEvent(id, 'delete')
    }
    return result
  }

  /**
   * Move a feature from one group to another.
   */
  async moveToGroup(featureId: string, fromGroupId: string, toGroupId: string): Promise<void> {
    const featureRef = nodeRef('feature', featureId)
    await edgeService.deleteExact(nodeRef('group', fromGroupId), 'has-feature', featureRef)
    await edgeService.create({
      subject: nodeRef('group', toGroupId),
      predicate: 'has-feature',
      object: featureRef,
    })
  }

  /**
   * Get all features for an entity (two-hop: entity → layer groups → features).
   */
  async getFeaturesForEntity(entityType: string, entityId: string): Promise<Feature[]> {
    const groupIds = await edgeService.getObjectIds(
      nodeRef(entityType, entityId),
      'has-layer-group'
    )
    if (groupIds.length === 0) return []

    const allFeatureIds: string[] = []
    for (const groupId of groupIds) {
      const featureIds = await edgeService.getObjectIds(nodeRef('group', groupId), 'has-feature')
      allFeatureIds.push(...featureIds)
    }

    if (allFeatureIds.length === 0) return []
    return db.features.where('id').anyOf(allFeatureIds).toArray()
  }

  /**
   * Get all features in a layer group.
   */
  async getFeaturesForGroup(groupId: string): Promise<Feature[]> {
    const featureIds = await edgeService.getObjectIds(nodeRef('group', groupId), 'has-feature')
    if (featureIds.length === 0) return []
    return db.features.where('id').anyOf(featureIds).toArray()
  }

  /**
   * Reverse two-hop: feature ← has-feature ← group ← has-layer-group ← activity.
   * Returns the activity ID if this feature belongs to an activity, or undefined.
   */
  async getParentActivity(featureId: string): Promise<string | undefined> {
    const groupIds = await edgeService.getSubjectIds(nodeRef('feature', featureId), 'has-feature')
    for (const groupId of groupIds) {
      const edges = await edgeService.getByObject(nodeRef('group', groupId), 'has-layer-group')
      for (const edge of edges) {
        const { type, id } = parseNodeRef(edge.subject)
        if (type === 'activity') return id
      }
    }
    return undefined
  }

  /**
   * Reverse two-hop to the owning activity OR plan:
   *   feature ← has-feature ← group ← has-layer-group ← (activity | plan).
   * Returns the owner's type and id, or undefined for a standalone feature.
   */
  async getParentEntity(
    featureId: string
  ): Promise<{ type: 'activity' | 'plan'; id: string } | undefined> {
    const groupIds = await edgeService.getSubjectIds(nodeRef('feature', featureId), 'has-feature')
    for (const groupId of groupIds) {
      const edges = await edgeService.getByObject(nodeRef('group', groupId), 'has-layer-group')
      for (const edge of edges) {
        const { type, id } = parseNodeRef(edge.subject)
        if (type === 'activity' || type === 'plan') return { type, id }
      }
    }
    return undefined
  }

  /** Resolve the root group ID for an entity */
  private async resolveRootGroupId(target: AddFeatureToEntityOptions): Promise<string | undefined> {
    const ids = await edgeService.getObjectIds(
      nodeRef(target.entityType, target.entityId),
      'has-layer-group'
    )
    return ids[0]
  }
}

export const featureStorage = new FeatureStorageService()
